import React, { useState, useEffect } from "react";
import axios from "axios";
import ViewIcon from "../../../../../icons/view";
import Trash from "../../../../../icons/trash";
import Status from "../../../../../icons/status";
import Swal from "sweetalert2";
import View from "./View";
import { formatToMMDDYYYY } from "../../../../../utils/dates";

type Reto = {
  reto_id: number;
  nombre: string;
  producto: string;
  fecha_inicio: string;
  fecha_fin: string;
  puntos: number;
  estado: string;
};

interface Props {
  counter: number;
  setCounter: React.Dispatch<React.SetStateAction<number>>;
}

const RetoList = ({ counter, setCounter }: Props) => {
  const [retos, setRetos] = useState<Reto[]>([]);
  const [retoView, setRetoView] = useState(false);
  const [retoViewID, setRetoViewID] = useState<number>(0);
  const [deleteCount, setDeleteCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const generatePageNumbers = () => {
    return Array.from(
      { length: 5 },
      (_, index) => currentPage - 2 + index
    ).filter((page) => page >= 1 && page <= totalPages);
  };

  const handlePageChange = (page: any) => {
    setCurrentPage(page);
    getRetos(page);
  };

  const getRetos = async (page = 1) => {
    try {
      const query = `page=${page}&search=${encodeURIComponent(
        searchText
      )}&state=${encodeURIComponent(statusFilter)}`;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/retos/?${query}`,
        {
          headers: { "auth-token": sessionStorage.getItem("token") },
        }
      );
      setCurrentPage(response.data.pageInfo.currentPage);
      setTotalPages(response.data.pageInfo.totalPages);
      setRetos(response.data.data);
    } catch (error) {
      if (error === "Error: Request failed with status code 404") {
        console.log("No existe el usuario");
      }
    }
  };

  const deletePremio = async (reto_id: any) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/retos/${reto_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "auth-token": `${sessionStorage.getItem("token")}`,
          },
        }
      );
      setDeleteCount((prevCount) => prevCount + 1);
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        text: "Se ha eliminado el reto",
        confirmButtonText: "Aceptar",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ha ocurrido un error al eliminar el reto",
        confirmButtonText: "Aceptar",
      });
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      getRetos(currentPage);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchText, statusFilter, deleteCount]);

  useEffect(() => {
    getRetos(currentPage);
  }, [counter]);

  return (
    <div className="px-2 flex flex-col">
      <div>
        <input
          type="text"
          placeholder="Buscar"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="main-input"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="main-dropdown my-2"
        >
          <option value="s">Todos</option>
          <option value="Activo">Activos</option>
          <option value="Inactivo">Inactivos</option>
        </select>
      </div>
      <div
        className="overflow-auto bg-white pb-8 md:pb-14"
        style={{ maxHeight: "calc(70vh)" }}
      >
        <table className="w-full grow">
          <thead className="bg-gray-50  text-left">
            <tr className="table-head sticky top-0 bg-gray-50">
              <th></th>
              <th>ID</th>
              <th>Nombre</th>
              <th>Producto</th>
              <th>Fecha de inicio</th>
              <th>Fecha final</th>
              <th className="text-center">Puntos</th>
              <th className="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {retos.length === 0 ? (
              <tr>
                <td className="text-center w-full" colSpan={6}>
                  No hay retos disponibles
                </td>
              </tr>
            ) : (
              retos.map((reto) => (
                <tr
                  className="hover:bg-slate-100 table-body"
                  key={reto.reto_id}
                >
                  <td>
                    <div className="m-auto w-fit">
                      <Status status={reto.estado} />
                    </div>
                  </td>
                  <td>{reto.reto_id}</td>
                  <td>{reto.nombre}</td>
                  <td>{reto.producto}</td>
                  <td>{formatToMMDDYYYY(reto.fecha_inicio)}</td>
                  <td>{formatToMMDDYYYY(reto.fecha_fin)}</td>
                  <td className="text-center">
                    {reto.puntos.toLocaleString()}
                  </td>
                  <td>
                    <div className="flex gap-2 m-auto w-fit">
                      <button
                        onClick={() => {
                          setRetoView(!retoView);
                          setRetoViewID(reto.reto_id);
                        }}
                      >
                        <ViewIcon />
                      </button>
                      <button
                        onClick={() => {
                          Swal.fire({
                            title: "¿Estás seguro?",
                            text: "No podrás revertir esta acción",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "Sí, eliminar",
                            cancelButtonText: "Cancelar",
                          }).then((result) => {
                            if (result.isConfirmed) {
                              deletePremio(reto.reto_id);
                            }
                          });
                        }}
                      >
                        <Trash size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div>
          {retos.length > 0 && (
            <div className="flex justify-center items-center gap-4 mt-4">
              <button
                className={`${
                  currentPage === 1 ? "hidden" : "cefa-button w-fit"
                }`}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Anterior
              </button>

              <div className="flex gap-2">
                {generatePageNumbers().map((page) => (
                  <button
                    key={page}
                    className={`cefa-button ${
                      currentPage === page ? "active" : ""
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                className={`${
                  currentPage === totalPages ? "hidden" : "cefa-button w-fit"
                }`}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      </div>
      <View
        retoView={retoView}
        setRetoView={setRetoView}
        retoViewID={retoViewID}
        setCounter={setCounter}
      />
    </div>
  );
};

export default RetoList;
